import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeSlash, Warning } from '@phosphor-icons/react';
import LienConnexionForm from '../components/shared/LienConnexionForm';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  // RP09 — « Recevoir un lien de connexion » (externes : sans mot de passe)
  const [lienMode, setLienMode] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/auth/password-reset-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch { /* */ }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12 bg-[#FAFAF8]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-16">
            <img 
              src="/images/logo-full.png" 
              alt="Kroquis" 
              className="h-14 object-contain"
            />
          </div>

          {/* Title */}
          <div className="mb-10">
            <h1 className="text-4xl tracking-tight mb-3 text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Connexion
            </h1>
            <p className="text-[#6B6B6B]">
              Accédez à votre espace de gestion de projets
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-[#FFEBEE] border border-[#8B3A3A] flex items-start gap-3" data-testid="login-error">
              <Warning size={20} weight="bold" className="text-[#8B3A3A] flex-shrink-0 mt-0.5" />
              <p className="text-[#8B3A3A] text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#4A4A4A]">
                Adresse email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@cabinet.fr"
                required
                className="h-12 border-[#E0E0DB] focus:border-[#2C2C2C] rounded-none bg-white"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#4A4A4A]">
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  required
                  className="h-12 border-[#E0E0DB] focus:border-[#2C2C2C] rounded-none pr-12 bg-white"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B6B6B] hover:text-[#2C2C2C]"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <button type="button" onClick={() => setForgotMode(true)} className="text-xs text-[#C75B39] hover:underline" data-testid="forgot-password-link">
                Mot de passe oublié ?
              </button>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#2C2C2C] hover:bg-[#4A4A4A] text-white font-medium rounded-none"
              data-testid="login-submit-button"
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          {/* Forgot Password Modal */}
          {forgotMode && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setForgotMode(false)}>
              <div className="bg-white p-8 shadow-xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
                {forgotSent ? (
                  <div className="text-center">
                    <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>Email envoyé</h2>
                    <p className="text-sm text-[#6B6B6B] mb-6">Si un compte existe avec cette adresse, un email de réinitialisation a été envoyé.</p>
                    <Button className="w-full rounded-none bg-[#2C2C2C] hover:bg-[#4A4A4A]" onClick={() => { setForgotMode(false); setForgotSent(false); }}>Retour à la connexion</Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword}>
                    <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>Mot de passe oublié</h2>
                    <p className="text-sm text-[#6B6B6B] mb-4">Entrez votre adresse email pour recevoir un lien de réinitialisation.</p>
                    <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="vous@cabinet.fr" className="h-12 rounded-none mb-4" required data-testid="forgot-email-input" />
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="flex-1 rounded-none" onClick={() => setForgotMode(false)}>Annuler</Button>
                      <Button type="submit" className="flex-1 rounded-none bg-[#C75B39] hover:bg-[#D97B5C]" disabled={forgotLoading} data-testid="forgot-submit-btn">
                        {forgotLoading ? 'Envoi...' : 'Envoyer'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* RP09 (D10 C) — externes : lien de connexion sans mot de passe */}
          <div className="mt-6 text-center">
            <button type="button" onClick={() => setLienMode(true)} className="text-sm text-[#C75B39] hover:underline" data-testid="lien-connexion-open">
              Recevoir un lien de connexion
            </button>
          </div>
          {lienMode && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setLienMode(false)}>
              <div className="bg-white p-8 shadow-xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
                <LienConnexionForm origine="bureau" emailInitial={email} onFermer={() => setLienMode(false)} />
              </div>
            </div>
          )}

          {/* S01 / PLA-03 — accès sur invitation uniquement */}
          <div className="mt-8 text-center">
            <p className="text-[#6B6B6B] text-sm">
              Pas encore de compte ? L'accès se fait sur invitation de votre architecte.
            </p>
          </div>

        </div>
      </div>

      {/* Right Panel - Image & Brand */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-[#2C2C2C] relative"
      >
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="max-w-lg text-center">
            <img 
              src="/images/logo-full.png" 
              alt="Kroquis" 
              className="h-24 object-contain mx-auto mb-12 brightness-0 invert"
            />
            <h2 className="text-3xl text-white mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Gestion de projets<br />d'architecture
            </h2>
            <p className="text-lg text-[#9A9A9A] leading-relaxed">
              Une plateforme complète pour piloter vos chantiers, 
              coordonner vos équipes et suivre vos projets de l'esquisse à la livraison.
            </p>
            
            <div className="mt-12 grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-[#C75B39]" style={{ fontFamily: 'DM Serif Display, serif' }}>ESQ</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Esquisse</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#D4A84B]" style={{ fontFamily: 'DM Serif Display, serif' }}>EXE</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Exécution</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#2D6A4F]" style={{ fontFamily: 'DM Serif Display, serif' }}>AOR</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Réception</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
